<template>
  <div class="admin-page">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Workspace Setup</p>
        <h1 class="page-title">Create organization</h1>
        <p class="page-subtitle">
          Start with the parent organization. Clinics or hospitals can be attached after this, so this form only asks for the essentials.
        </p>
      </div>
      <button class="ui-button secondary" @click="$router.push('/org')">
        <i class="fas fa-arrow-left"></i> Back to organizations
      </button>
    </div>

    <div class="ui-card">
      <div class="ui-card-body">
        <div v-if="validationError" class="alert-message">
          <i class="fas fa-exclamation-circle"></i> {{ validationError }}
        </div>

        <section>
          <div class="section-heading">
            <div>
              <h2 class="section-title">Organization basics</h2>
              <p class="section-description">Required now. Everything else can be completed when the real details are ready.</p>
            </div>
          </div>

          <div class="form-grid">
            <div class="form-section">
              <label class="field-label">Organization name <span class="required">*</span></label>
              <input class="ui-input" v-model="org_data.organization_name" placeholder="e.g. Dr Mata Group">
            </div>

            <div class="form-section">
              <label class="field-label">Organization type <span class="required">*</span></label>
              <select class="ui-select" v-model="org_data.organization_type">
                <option value="clinic">Clinic group</option>
                <option value="hospital">Hospital group</option>
              </select>
            </div>

            <div class="form-section">
              <label class="field-label">Fundus camera</label>
              <select class="ui-select" v-model="org_data.fundus_camera_uuid">
                <option value="">Not assigned yet</option>
                <option
                  v-for="camera in fundusCameraOptions"
                  :key="camera.FundusCameraUuid"
                  :value="camera.FundusCameraUuid"
                >
                  {{ camera.DisplayName || `${camera.Brand} : ${camera.Model}` }}
                </option>
              </select>
              <span class="field-hint">Optional. This can be updated later.</span>
            </div>

            <div class="form-section">
              <label class="field-label">Subscription type</label>
              <select class="ui-select" v-model="org_data.subscription_type">
                <option value="">Not decided yet</option>
                <option v-for="type in subscriptionTypes" :key="type" :value="type">{{ type }}</option>
              </select>
              <span class="field-hint">Optional. This can be updated later.</span>
            </div>

            <div class="form-section">
              <label class="field-label">Contact email</label>
              <input type="email" class="ui-input" v-model="org_data.contact_email" placeholder="admin@organization.com">
            </div>
          </div>
        </section>

        <section class="mt-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Optional details</h2>
              <p class="section-description">Useful for records, but not needed to create the organization.</p>
            </div>
            <span class="optional-pill">Optional</span>
          </div>

          <div class="form-grid">
            <div class="form-section full-width">
              <label class="field-label">HQ address</label>
              <textarea class="ui-textarea" v-model="org_data.hq_address" placeholder="Headquarters address"></textarea>
            </div>
            <div class="form-section">
              <label class="field-label">City</label>
              <input class="ui-input" v-model="org_data.city" placeholder="e.g. Kuala Lumpur">
            </div>
            <div class="form-section">
              <label class="field-label">State</label>
              <select class="ui-select" v-model="org_data.state">
                <option value="">Select state</option>
                <option v-for="state in states" :key="state" :value="state">{{ state }}</option>
              </select>
            </div>
            <div class="form-section">
              <label class="field-label">Postal code</label>
              <input class="ui-input" v-model="org_data.postal_code" placeholder="e.g. 50450">
            </div>
            <div class="form-section">
              <label class="field-label">Contact phone</label>
              <input class="ui-input" v-model="org_data.contact_phone" placeholder="Digits only" @input="allowOnlyDigits">
            </div>
          </div>
        </section>

        <div class="ui-actions">
          <button class="ui-button secondary" @click="$router.push('/org')">Cancel</button>
          <button class="ui-button primary" @click="submit()" :disabled="isSubmitting">
            <i class="fas" :class="isSubmitting ? 'fa-spinner fa-spin' : 'fa-plus'"></i>
            {{ isSubmitting ? 'Creating...' : 'Create organization' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFundusCameras, registerOrg } from "@/services/orgServices"
import StatesJSON from "@/constants/states"

export default {
  created() {
    this.loadFundusCameras();
  },

  data() {
    return {
      org_data: {
        organization_name: "",
        organization_type: "clinic",
        hq_address: "",
        city: "",
        state: "",
        country: "Malaysia",
        postal_code: "",
        latitude: "",
        longitude: "",
        contact_email: "",
        contact_phone: "",
        subscription_plan: "",
        subscription_type: "",
        subscription_status: "",
        fundus_camera_uuid: ""
      },
      fundusCameraOptions: [],
      states: StatesJSON,
      subscriptionTypes: ['Government', 'Private'],
      validationError: null,
      isSubmitting: false,
    }
  },

  methods: {
    async loadFundusCameras() {
      try {
        const response = await getFundusCameras();
        this.fundusCameraOptions = response.data || [];
      } catch (error) {
        console.log(error);
      }
    },

    validateForm() {
      if (!this.org_data.organization_name.trim()) {
        this.validationError = "Organization name is required.";
        return false;
      }
      if (!this.org_data.organization_type) {
        this.validationError = "Organization type is required.";
        return false;
      }
      this.validationError = null;
      return true;
    },

    allowOnlyDigits(event) {
      this.org_data.contact_phone = event.target.value.replace(/\D/g, '');
    },

    async submit() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;
      try {
        const response = await registerOrg(this.org_data);
        if(response) {
          this.$router.push('/org');
        }
      } catch (error) {
        console.log(error);
        this.validationError = "Unable to create organization. Please try again.";
      } finally {
        this.isSubmitting = false;
      }
    }
  }
}
</script>
